import type { IdeaAlertModel } from '.';
import { signal, computed } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { IdeaAlertDefinition } from '@thinkalpha/platform-ws-client/contracts/alerts.js';
import { injectable, inject } from 'src/features/ioc';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Implementation of IdeaAlertModel.
 * Manages idea alert definition state.
 */
@injectable()
export class IdeaAlertModelImpl implements IdeaAlertModel, Disposable {
    readonly #definition = signal<IdeaAlertDefinition>(null!);

    #id: string = '';
    #initialized = false;

    readonly slowIdeaId: ReadonlySignal<string | null>;
    readonly fastIdeaId: ReadonlySignal<string | null>;
    readonly triggerMode: ReadonlySignal<'enter' | 'exit' | 'all'>;

    constructor(@inject('Logger') private logger: ReactBindings['Logger']) {
        this.logger = logger.getSubLogger({ name: 'IdeaAlertModel' });
        this.slowIdeaId = computed(() => this.#definition.value?.slowIdeaId ?? null);
        this.fastIdeaId = computed(() => this.#definition.value?.fastIdeaId ?? null);
        this.triggerMode = computed(() => this.#definition.value?.triggerMode ?? 'enter');
    }

    get id(): string {
        return this.#id;
    }

    get definition(): ReadonlySignal<IdeaAlertDefinition> {
        return this.#definition;
    }

    /**
     * Initialize the model from an idea alert definition.
     */
    initFromDefinition(definition: IdeaAlertDefinition): void {
        if (this.#initialized) {
            this.logger.warn('IdeaAlertModel already initialized', { alertId: this.#id });
            return;
        }
        this.#initialized = true;

        this.#id = definition.id;
        this.#definition.value = definition;

        if (TRACE) {
            this.logger.trace('IdeaAlertModel initialized', {
                alertId: this.#id,
                slowIdeaId: definition.slowIdeaId,
                fastIdeaId: definition.fastIdeaId,
                triggerMode: definition.triggerMode,
            });
        }
    }

    /**
     * Update the model with a new definition (e.g., when alert is modified).
     */
    updateDefinition(definition: IdeaAlertDefinition): void {
        this.#definition.value = definition;
    }

    readonly [Symbol.dispose] = () => {
        /* no resources to clean up */
    };
}
